import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { ForecastLoader } from '../components/ForecastLoader';
import { getGlobalForecast, getClients, getScenarioForecast } from '../lib/api';
import { formatNumber, formatCurrency, formatPercent, chartColors, routeColors, formatMonth } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Info, 
  SlidersHorizontal,
  RotateCcw,
  DollarSign,
  Package,
  X,
  Calendar,
  CalendarDays,
  CalendarRange
} from 'lucide-react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ComposedChart,
} from 'recharts';

const ForecastGlobalPage = () => {
  // Forecast data
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // View mode: 'monthly', 'weekly', 'yearly'
  const [viewMode, setViewMode] = useState('monthly');
  
  // Scenario state
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [scenarioData, setScenarioData] = useState(null);
  const [loadingScenario, setLoadingScenario] = useState(false);
  const [showScenarios, setShowScenarios] = useState(false);
  const [scenarioPanelOpen, setScenarioPanelOpen] = useState(false);
  
  const defaultModifiers = { pessimistic: -15, optimistic: 20 };
  const [modifiers, setModifiers] = useState(defaultModifiers);
  
  // CRITICAL: Track which modifiers were used for current scenarioData
  // This prevents showing stale data with new percentages
  const [appliedModifiers, setAppliedModifiers] = useState(null);
  const [appliedClient, setAppliedClient] = useState(null);
  
  // Check if current data matches current settings
  const isDataStale = showScenarios && (
    !appliedModifiers ||
    appliedModifiers.pessimistic !== modifiers.pessimistic ||
    appliedModifiers.optimistic !== modifiers.optimistic ||
    appliedClient !== selectedClient
  );

  // Transform data based on view mode
  const transformedData = useMemo(() => {
    if (!forecastData?.data) return [];
    
    const monthlyData = forecastData.data;
    
    if (viewMode === 'monthly') {
      return monthlyData;
    }
    
    if (viewMode === 'weekly') {
      // Convert monthly to weekly (approximate: divide by 4.33)
      const weeklyData = [];
      monthlyData.forEach((month, idx) => {
        const weeksInMonth = 4;
        const weeklyActual = month.actual ? Math.round(month.actual / weeksInMonth) : null;
        const weeklyForecast = month.forecast ? Math.round(month.forecast / weeksInMonth) : null;
        const weeklyLower = month.lower ? Math.round(month.lower / weeksInMonth) : null;
        const weeklyUpper = month.upper ? Math.round(month.upper / weeksInMonth) : null;
        
        for (let w = 1; w <= weeksInMonth; w++) {
          weeklyData.push({
            month: `${month.month}-S${w}`,
            displayLabel: `S${w}`,
            parentMonth: month.month,
            actual: weeklyActual,
            forecast: weeklyForecast,
            lower: weeklyLower,
            upper: weeklyUpper,
            isWeekly: true
          });
        }
      });
      return weeklyData;
    }
    
    if (viewMode === 'yearly') {
      // Aggregate by year
      const yearlyMap = {};
      monthlyData.forEach(month => {
        const year = month.month.substring(0, 4);
        if (!yearlyMap[year]) {
          yearlyMap[year] = { month: year, actual: 0, forecast: 0, lower: 0, upper: 0, hasActual: false, hasForecast: false };
        }
        if (month.actual) {
          yearlyMap[year].actual += month.actual;
          yearlyMap[year].hasActual = true;
        }
        if (month.forecast) {
          yearlyMap[year].forecast += month.forecast;
          yearlyMap[year].hasForecast = true;
        }
        if (month.lower) yearlyMap[year].lower += month.lower;
        if (month.upper) yearlyMap[year].upper += month.upper;
      });
      
      return Object.values(yearlyMap).map(y => ({
        month: y.month,
        actual: y.hasActual ? Math.round(y.actual) : null,
        forecast: y.hasForecast ? Math.round(y.forecast) : null,
        lower: y.lower ? Math.round(y.lower) : null,
        upper: y.upper ? Math.round(y.upper) : null,
        isYearly: true
      }));
    }
    
    return monthlyData;
  }, [forecastData, viewMode]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [forecastRes, clientsRes] = await Promise.all([
          getGlobalForecast(),
          getClients()
        ]);
        setForecastData(forecastRes.data);
        setClients(clientsRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch scenarios when needed
  useEffect(() => {
    if (!showScenarios) return;
    
    const fetchScenario = async () => {
      setLoadingScenario(true);
      try {
        const currentModifiers = { ...modifiers };
        const currentClient = selectedClient;
        
        const response = await getScenarioForecast({
          client_id: currentClient === 'all' ? null : currentClient,
          pessimistic_modifier: currentModifiers.pessimistic / 100,
          base_modifier: 0,
          optimistic_modifier: currentModifiers.optimistic / 100,
        });
        
        // Only update if these are still the current settings
        setScenarioData(response.data);
        setAppliedModifiers(currentModifiers);
        setAppliedClient(currentClient);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoadingScenario(false);
      }
    };
    
    const debounceTimer = setTimeout(fetchScenario, 300);
    return () => clearTimeout(debounceTimer);
  }, [showScenarios, selectedClient, modifiers]);

  // Reset scenarios
  const handleResetScenarios = () => {
    setModifiers(defaultModifiers);
    setSelectedClient('all');
  };

  // Clear scenarios (back to baseline)
  const handleClearScenarios = () => {
    setShowScenarios(false);
    setScenarioData(null);
    setAppliedModifiers(null);
    setAppliedClient(null);
    setModifiers(defaultModifiers);
    setSelectedClient('all');
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Forecast Global</h1>
            <p className="text-slate-500 mt-1">Cargando predicciones...</p>
          </div>
          <ForecastLoader type="forecast" />
        </div>
      </Layout>
    );
  }

  // Prepare baseline chart data - uses transformedData based on viewMode
  const baselineChartData = transformedData?.map(d => ({
    month: d.isWeekly ? d.month : d.isYearly ? d.month : formatMonth(d.month),
    actual: d.actual,
    forecast: d.forecast,
    lower_80: d.lower || d.lower_80,
    upper_80: d.upper || d.upper_80,
  })) || [];

  // Find forecast start
  const forecastStartIndex = baselineChartData.findIndex(d => d.forecast && !d.actual);

  // Route breakdown for pie chart
  const routeData = Object.entries(forecastData?.by_route || {}).map(([route, value]) => ({
    name: route,
    value: Math.round(value),
  }));

  // YoY comparison
  const yoy = forecastData?.yoy_comparison || {};
  const yoyChange = yoy.change_percent || 0;

  // Prepare scenario chart data (combined with baseline)
  const scenarioChartData = [];
  if (showScenarios && scenarioData) {
    scenarioData?.historical?.forEach(h => {
      scenarioChartData.push({
        month: formatMonth(h.month),
        actual: h.actual,
        isHistorical: true,
      });
    });
    
    scenarioData?.scenarios?.base?.forEach((b, i) => {
      const pess = scenarioData.scenarios.pessimistic[i];
      const opt = scenarioData.scenarios.optimistic[i];
      scenarioChartData.push({
        month: formatMonth(b.month),
        base: b.forecast,
        pessimistic: pess.forecast,
        optimistic: opt.forecast,
        isHistorical: false,
      });
    });
  }

  const revenue = scenarioData?.revenue_impact || {};

  return (
    <Layout>
      <div className="space-y-8" data-testid="forecast-global-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Forecast Global</h1>
            <p className="text-slate-500 mt-1">
              {showScenarios 
                ? `Simulación: ${selectedClient === 'all' ? 'Todos los clientes' : clients.find(c => c.id === selectedClient)?.name}` 
                : 'Proyección de volumen agregado de toda la cartera'}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {showScenarios && (
              <Button 
                variant="outline" 
                onClick={handleClearScenarios}
                className="gap-2"
                data-testid="clear-scenarios-btn"
              >
                <X className="w-4 h-4" />
                Volver al Baseline
              </Button>
            )}
            
            <Sheet open={scenarioPanelOpen} onOpenChange={setScenarioPanelOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant={showScenarios ? "default" : "outline"} 
                  className="gap-2"
                  data-testid="open-scenario-panel-btn"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Simulador de Escenarios
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[400px] sm:w-[540px]">
                <SheetHeader>
                  <SheetTitle>Simulador de Escenarios</SheetTitle>
                  <SheetDescription>
                    Ajusta los parámetros para proyectar escenarios pesimista, base y optimista.
                  </SheetDescription>
                </SheetHeader>
                
                <div className="mt-6 space-y-6">
                  {/* Client selector */}
                  <div className="space-y-2">
                    <Label>Alcance de la simulación</Label>
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger data-testid="scenario-client-select">
                        <SelectValue placeholder="Seleccionar cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los clientes (Global)</SelectItem>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pessimistic Modifier */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-rose-500" />
                        Escenario Pesimista
                      </Label>
                      <span className="text-sm font-semibold text-rose-600">{modifiers.pessimistic}%</span>
                    </div>
                    <Slider
                      value={[modifiers.pessimistic]}
                      onValueChange={([v]) => setModifiers({...modifiers, pessimistic: v})}
                      min={-50}
                      max={0}
                      step={5}
                      data-testid="pessimistic-slider"
                    />
                    <p className="text-xs text-slate-500">Variación negativa sobre el forecast base</p>
                  </div>

                  {/* Base indicator */}
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Escenario Base</span>
                      <span className="text-xs text-slate-500 ml-auto">Forecast sin modificar</span>
                    </div>
                  </div>

                  {/* Optimistic Modifier */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        Escenario Optimista
                      </Label>
                      <span className="text-sm font-semibold text-emerald-600">+{modifiers.optimistic}%</span>
                    </div>
                    <Slider
                      value={[modifiers.optimistic]}
                      onValueChange={([v]) => setModifiers({...modifiers, optimistic: v})}
                      min={0}
                      max={50}
                      step={5}
                      data-testid="optimistic-slider"
                    />
                    <p className="text-xs text-slate-500">Variación positiva sobre el forecast base</p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-4 border-t border-slate-200">
                    <Button 
                      variant="outline" 
                      onClick={handleResetScenarios}
                      className="flex-1 gap-2"
                      data-testid="reset-modifiers-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Resetear Valores
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowScenarios(true);
                        setScenarioPanelOpen(false);
                      }}
                      className="flex-1"
                      data-testid="apply-scenario-btn"
                    >
                      Aplicar Simulación
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* YoY Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <p className="text-sm text-slate-500 mb-2">{yoy.current_year || new Date().getFullYear()} (YTD)</p>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(yoy.current_year_total || 0)}</p>
            <p className="text-xs text-slate-400 mt-1">kgs acumulados ({yoy.comparison_months || 0} meses)</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <p className="text-sm text-slate-500 mb-2">{yoy.last_year || new Date().getFullYear() - 1} (mismo período)</p>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(yoy.last_year_ytd_total || yoy.last_year_total || 0)}</p>
            <p className="text-xs text-slate-400 mt-1">kgs acumulados ({yoy.comparison_months || 0} meses)</p>
          </div>
          <div className={`rounded-xl border p-6 shadow-sm ${
            yoyChange >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
          }`}>
            <p className="text-sm text-slate-500 mb-2">Variación YoY</p>
            <div className="flex items-center gap-2">
              {yoyChange > 0 ? (
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              ) : yoyChange < 0 ? (
                <TrendingDown className="w-6 h-6 text-rose-600" />
              ) : (
                <Minus className="w-6 h-6 text-slate-600" />
              )}
              <p className={`text-3xl font-bold ${yoyChange >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {yoyChange >= 0 ? '+' : ''}{yoyChange.toFixed(1)}%
              </p>
            </div>
            <p className="text-xs text-slate-500 mt-1">comparación YTD vs mismo período</p>
          </div>
        </div>

        {/* Scenario Revenue Impact (only when scenarios active) */}
        {showScenarios && scenarioData && (
          <div className="relative">
            {(loadingScenario || isDataStale) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-xl z-10">
                <div className="flex items-center gap-2 text-slate-600 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm font-medium">Recalculando escenarios...</span>
                </div>
              </div>
            )}
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${(loadingScenario || isDataStale) ? 'opacity-40 pointer-events-none' : ''}`}>
              <div className="bg-rose-50 rounded-xl border border-rose-200 p-5">
                <div className="flex items-center gap-2 text-rose-600 mb-2">
                  <TrendingDown className="w-5 h-5" />
                  <span className="text-sm font-medium">Pesimista ({appliedModifiers?.pessimistic ?? modifiers.pessimistic}%)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-2xl font-bold text-rose-700">{formatNumber(revenue.pessimistic_kgs || 0)}</p>
                    <p className="text-xs text-rose-500">kgs proyectados</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-lg font-semibold text-rose-600">{formatCurrency(revenue.pessimistic || 0)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <Minus className="w-5 h-5" />
                  <span className="text-sm font-medium">Base (sin cambios)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{formatNumber(revenue.base_kgs || 0)}</p>
                    <p className="text-xs text-slate-500">kgs proyectados</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-lg font-semibold text-slate-700">{formatCurrency(revenue.base || 0)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
                <div className="flex items-center gap-2 text-emerald-600 mb-2">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm font-medium">Optimista (+{appliedModifiers?.optimistic ?? modifiers.optimistic}%)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">{formatNumber(revenue.optimistic_kgs || 0)}</p>
                    <p className="text-xs text-emerald-500">kgs proyectados</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-lg font-semibold text-emerald-600">{formatCurrency(revenue.optimistic || 0)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Chart */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {showScenarios ? 'Comparativa de Escenarios' : `Histórico + Forecast 12 Meses`}
              </h2>
              <p className="text-sm text-slate-500">
                {showScenarios 
                  ? 'Proyección con escenarios pesimista, base y optimista'
                  : `Vista ${viewMode === 'weekly' ? 'semanal' : viewMode === 'yearly' ? 'anual' : 'mensual'} con intervalos de confianza 80%`}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* View Mode Selector */}
              <div className="flex items-center bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('weekly')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'weekly' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <CalendarDays className="w-4 h-4" />
                  Semanal
                </button>
                <button
                  onClick={() => setViewMode('monthly')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'monthly' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Mensual
                </button>
                <button
                  onClick={() => setViewMode('yearly')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'yearly' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <CalendarRange className="w-4 h-4" />
                  Anual
                </button>
              </div>
              
              {/* Legend */}
              <div className="flex items-center gap-4 text-sm">
                {showScenarios ? (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartColors.primary }} />
                      <span className="text-slate-600">Histórico</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-rose-500" />
                      <span className="text-slate-600">Pesimista</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-500" />
                      <span className="text-slate-600">Base</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-slate-600">Optimista</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartColors.primary }} />
                      <span className="text-slate-600">Histórico</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartColors.secondary }} />
                      <span className="text-slate-600">Forecast</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="h-96">
            {loadingScenario ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
              </div>
            ) : showScenarios && scenarioChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={scenarioChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatNumber(v)}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [formatNumber(value) + ' kgs', '']}
                  />
                  
                  {scenarioChartData.findIndex(d => !d.isHistorical) > 0 && (
                    <ReferenceLine 
                      x={scenarioChartData[scenarioChartData.findIndex(d => !d.isHistorical) - 1]?.month} 
                      stroke="#94a3b8" 
                      strokeDasharray="5 5"
                      label={{ value: 'Inicio Forecast', position: 'top', fill: '#94a3b8', fontSize: 11 }}
                    />
                  )}
                  
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke={chartColors.primary}
                    strokeWidth={2}
                    dot={{ fill: chartColors.primary, strokeWidth: 0, r: 3 }}
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="pessimistic"
                    stroke="#ef4444"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#ef4444', strokeWidth: 0, r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="base"
                    stroke="#64748b"
                    strokeWidth={2}
                    dot={{ fill: '#64748b', strokeWidth: 0, r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="optimistic"
                    stroke="#10b981"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#10b981', strokeWidth: 0, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={baselineChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActualGlobal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorForecastGlobal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.secondary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={chartColors.secondary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatNumber(v)}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [formatNumber(value) + ' kgs', '']}
                  />
                  {forecastStartIndex > 0 && (
                    <ReferenceLine 
                      x={baselineChartData[forecastStartIndex - 1]?.month} 
                      stroke="#94a3b8" 
                      strokeDasharray="5 5"
                      label={{ value: 'Inicio Forecast', position: 'top', fill: '#94a3b8', fontSize: 11 }}
                    />
                  )}
                  {/* 80% CI */}
                  <Area
                    type="monotone"
                    dataKey="upper_80"
                    stroke="transparent"
                    fill={chartColors.secondary}
                    fillOpacity={0.1}
                  />
                  <Area
                    type="monotone"
                    dataKey="actual"
                    stroke={chartColors.primary}
                    strokeWidth={2}
                    fill="url(#colorActualGlobal)"
                    dot={{ fill: chartColors.primary, strokeWidth: 0, r: 3 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="forecast"
                    stroke={chartColors.secondary}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    fill="url(#colorForecastGlobal)"
                    dot={{ fill: chartColors.secondary, strokeWidth: 0, r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Two Column Layout - Route breakdown and Factors */}
        {!showScenarios && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Route Breakdown */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-6">Desglose por Ruta</h2>
              <div className="flex items-center gap-8">
                <div className="w-48 h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={routeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {routeData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={routeColors[entry.name] || chartColors.primary} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [formatNumber(value) + ' kgs', '']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {routeData.slice(0, 6).map((route) => {
                    const total = routeData.reduce((acc, r) => acc + r.value, 0);
                    const percentage = total > 0 ? (route.value / total * 100) : 0;
                    return (
                      <div key={route.name} className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: routeColors[route.name] || chartColors.primary }}
                        />
                        <span className="text-sm text-slate-600 flex-1">{route.name}</span>
                        <span className="text-sm font-medium text-slate-900">{formatPercent(percentage, 0)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Model Factors */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Factores del Modelo</h2>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Info className="w-3.5 h-3.5" />
                  <span>Peso relativo</span>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={forecastData?.top_factors || []} 
                    layout="vertical"
                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis 
                      type="number" 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                      domain={[0, 30]}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="factor" 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                      width={150}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name, props) => [
                        `${value}%`,
                        props.payload.description
                      ]}
                    />
                    <Bar 
                      dataKey="weight" 
                      fill={chartColors.primary} 
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ForecastGlobalPage;
